/**
 * SidebarTabs — the tab strip + content panes for the right sidebar.
 *
 * Pulled into its own component so both the desktop docked sidebar and the
 * mobile bottom sheet render the same tabs without duplicating layout.
 */
import * as Tabs from '@radix-ui/react-tabs';
import { Calendar as CalendarIcon, Mail, CheckSquare } from 'lucide-react';
import { CalendarTab } from './CalendarTab';
import { EmailTab } from './EmailTab';

type SidebarTab = 'calendar' | 'email' | 'tasks';

interface Props {
  active: SidebarTab;
  onChange: (t: SidebarTab) => void;
}

export function SidebarTabs({ active, onChange }: Props) {
  return (
    <Tabs.Root
      value={active}
      onValueChange={(v) => onChange(v as SidebarTab)}
      className="flex h-full min-h-0 flex-1 flex-col"
    >
      <Tabs.List className="flex shrink-0 items-center gap-1 border-b border-white/10 px-2 pt-2">
        <SidebarTabTrigger value="calendar" icon={CalendarIcon} label="Calendar" />
        <SidebarTabTrigger value="email" icon={Mail} label="Email" />
        <SidebarTabTrigger value="tasks" icon={CheckSquare} label="Tasks" />
      </Tabs.List>

      <Tabs.Content value="calendar" className="min-h-0 flex-1 overflow-hidden outline-none">
        <CalendarTab />
      </Tabs.Content>
      <Tabs.Content value="email" className="min-h-0 flex-1 overflow-hidden outline-none">
        <EmailTab />
      </Tabs.Content>
      <Tabs.Content value="tasks" className="min-h-0 flex-1 overflow-y-auto px-4 py-6 outline-none">
        <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-xs text-neutral-500">
          Tasks land here in the next iteration — CRM tasks + manual to-dos in
          one rolling list.
        </div>
      </Tabs.Content>
    </Tabs.Root>
  );
}

function SidebarTabTrigger({
  value,
  icon: Icon,
  label,
}: {
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <Tabs.Trigger
      value={value}
      className="group flex flex-1 items-center justify-center gap-1.5 rounded-t-lg border-b-2 border-transparent px-2 py-2 text-xs font-medium text-neutral-400 transition hover:text-neutral-100 data-[state=active]:border-brand-400 data-[state=active]:text-brand-200"
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </Tabs.Trigger>
  );
}
